// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface NavigationItem {
  label: string;
  link: string;
  active?: boolean;
  pageType?: string; // Optional pageType for click handler
}

export interface Logo {
  image?: string;
  alt?: string;
  text?: string;
  link?: string;
}

export interface CTAButton {
  text: string;
  link?: string;
  backgroundColor?: string;
  textColor?: string;
}

/**
 * Dark themed page header component used in the website builder for preview and editor modes; displays logo, navigation, CTA and handles mobile menu and navigation interactions.
 * @component
 * @selector app-dark-page-header
 * @example
 *   <app-dark-page-header
 *     [logo]="logoObj"
 *     [navigation]="navItems"
 *     [ctaButton]="cta"
 *     [visible]="true"
 *     [clickable]="false"
 *     [selected]="false"
 *     [headingColor]="'#fff'"
 *     [headingFont]="'Inter'"
 *     [backgroundColor]="'#000'"
 *     (headerClick)="onHeader()"
 *     (navigationClick)="onNavigate($event)">
 *   </app-dark-page-header>
 *
 * This component coordinates rendering a dark-themed header and provides UX for:
 * - Rendering a logo, navigation links and an optional CTA button.
 * - Handling header-level clicks (when clickable) while ignoring clicks on navigation/CTA/menu controls.
 * - Emitting navigation events normalized to a pageType string inferred from item.pageType, hash links, known patterns, or URL query/path.
 * - Managing mobile menu open/close state and closing it on outside click or Escape key.
 * - Preventing duplicate navigation clicks and providing immediate visual feedback by managing a local activeNavigationIndex.
 *
 * Key methods:
 * - onHeaderClick(event: Event): Emits headerClick if header is clickable and the click is not on excluded controls.
 * - onNavigationItemClick(item: NavigationItem, event: Event): Normalizes link/page type, updates local active state, emits navigationClick, closes mobile menu and prevents duplicate clicks.
 * - onCTAButtonClick(event: Event): Maps CTA link to a pageType (using known patterns or matching navigation items) and emits navigationClick.
 * - toggleMobileMenu(event: Event): Toggles mobile menu open state (works in both editor and preview modes).
 * - closeMobileMenu(): Closes the mobile menu.
 * - onDocumentClick(event: Event): Host listener to close mobile menu when clicking outside.
 * - onEscapeKey(): Host listener to close mobile menu on Escape key.
 * - getHeadingColor(): Returns heading color from branding or default (#FFFFFF).
 * - getHeadingFont(): Returns heading font from branding or default ('Inter').
 *
 * Important lifecycle hooks:
 * - ngOnChanges(changes: SimpleChanges): Synchronizes local active navigation index when the navigation input changes (sets local active index to the input's active item or defaults to the first item).
 *
 * @input Logo | null logo - Optional logo object displayed in the header (can be null).
 * @input NavigationItem[] navigation - Array of navigation items to render; each item can include link, pageType and active flags.
 * @input CTAButton | null ctaButton - Optional CTA button data (link/text) displayed in the header.
 * @input boolean visible - Controls overall visibility of the header; defaults to true.
 * @input boolean clickable - When true the header responds to header clicks (editor mode); when false acts as preview (default false).
 * @input boolean selected - Indicates selected state for editor tooling (affects visual styling).
 * @input string | null headingColor - Optional branding color for heading text; falls back to white for dark theme.
 * @input string | null headingFont - Optional branding font for heading text; falls back to 'Inter'.
 * @input string | null backgroundColor - Optional background color for the header.
 * @output EventEmitter<void> headerClick - Emitted when the header (outside of controls) is clicked and clickable is true.
 * @output EventEmitter<string> navigationClick - Emitted with a normalized pageType string when a navigation item or CTA is activated.
 */
@Component({
  selector: 'app-dark-page-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.clickable]': 'clickable',
    '[class.preview-mode]': '!clickable',
    '[class.is-tablet]': "deviceType === 'tablet'",
    '[class.is-mobile]': "deviceType === 'mobile'",
  },
})
export class DarkPageHeaderComponent implements OnChanges {
  @Input() logo: Logo | null = null;
  @Input() navigation: NavigationItem[] = [];
  @Input() ctaButton: CTAButton | null = null;
  @Input() visible = true;
  @Input() clickable = false;
  @Input() selected = false;
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() backgroundColor: string | null = null; // Background color for header
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Output() headerClick = new EventEmitter<void>();
  @Output() navigationClick = new EventEmitter<string>();

  // Mobile menu state
  isMobileMenuOpen = false;

  // Prevent duplicate clicks
  private isNavigating = false;

  // Track active navigation item locally for immediate visual feedback
  private activeNavigationIndex: number | null = null;

  /**
   * Handle click events on the page header; if clickable and the clicked target is not a navigation or menu element, emit the headerClick event.
   * @example
   * nHeaderClick(event)
   * undefined
   * @param {Event} event - The click event received from the header element.
   * @returns {void} Does not return a value; emits headerClick when the header (outside excluded child elements) is clicked.
   **/
  onHeaderClick(event: Event): void {
    if (this.clickable) {
      // Don't propagate if clicking on navigation items or menu button
      const target = event.target as HTMLElement;
      if (
        target.closest('.nav-link') ||
        target.closest('.book-wash-btn') ||
        target.closest('.hamburger-menu') ||
        target.closest('.mobile-menu-overlay') ||
        target.closest('.mobile-menu-drawer')
      ) {
        return;
      }
      this.headerClick.emit();
    }
  }

  /**
   * Handle a navigation item click: prevents default and propagation, avoids duplicate navigation, resolves pageType from item.pageType or item.link (with normalization and pattern mapping), updates active navigation state for immediate visual feedback, emits a navigationClick event, closes mobile menu, and resets navigation flag after a short delay.
   * @example
   * nNavigationItemClick(item, event)
   * void
   * @param {{NavigationItem}} {{item}} - The navigation item that was clicked.
   * @param {{Event}} {{event}} - The click event; used to preventDefault and stopPropagation.
   * @returns {{void}} No return value; function emits navigationClick with the resolved pageType.
   **/
  onNavigationItemClick(item: NavigationItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    // Prevent duplicate clicks
    if (this.isNavigating) {
      return;
    }
    this.isNavigating = true;

    // Update active state immediately for visual feedback
    const clickedIndex = this.navigation.findIndex(nav => nav === item);
    if (clickedIndex !== -1) {
      this.activeNavigationIndex = clickedIndex;
      // Also update the item's active property for immediate visual update
      this.navigation.forEach((nav, index) => {
        nav.active = index === clickedIndex;
      });
    }

    // Use pageType if available (preferred method)
    let pageType = item.pageType;

    if (!pageType && item.link) {
      // Remove hash, leading/trailing slashes and normalize
      const normalizedLink = item.link
        .replace(/^#+|\/+$/g, '')
        .replace(/^\/+/, '')
        .toLowerCase();

      // Map common link patterns to pageType
      if (normalizedLink === '' || normalizedLink === 'home' || normalizedLink === '/') {
        pageType = 'home';
      } else if (
        normalizedLink === 'expresswashes' ||
        normalizedLink === 'express-washes' ||
        normalizedLink.includes('express-wash')
      ) {
        pageType = 'express-wash-packages';
      } else if (
        normalizedLink === 'expressdetails' ||
        normalizedLink === 'express-details' ||
        normalizedLink.includes('express-detail') ||
        normalizedLink.includes('express-auto')
      ) {
        pageType = 'express-auto-details';
      } else if (
        normalizedLink === 'fleets' ||
        normalizedLink === 'fleet' ||
        normalizedLink.includes('fleet')
      ) {
        pageType = 'fleet-management';
      } else if (
        normalizedLink === 'coupons' ||
        normalizedLink === 'coupon' ||
        normalizedLink.includes('coupon')
      ) {
        pageType = 'coupons';
      } else {
        // Try to extract pageType from query parameters: /preview?websiteId=...&pageType=...
        const urlParams = new URLSearchParams(item.link.split('?')[1] || '');
        pageType = urlParams.get('pageType') || undefined;

        // If still not found, try to extract from path: /website-preview/{templateId}/{pageType}
        if (!pageType) {
          const match = item.link.match(/\/website-preview\/[^/]+(?:\/(.+))?$/);
          if (match) {
            pageType = match[1] || 'home';
          } else {
            // Fallback: use the normalized link as pageType
            pageType = normalizedLink || 'home';
          }
        }
      }
    }

    // Default to home if still no pageType found
    if (!pageType) {
      pageType = 'home';
    }

    this.navigationClick.emit(pageType);
    // Close mobile menu when navigation item is clicked
    this.isMobileMenuOpen = false;

    // Reset navigation flag after a short delay
    setTimeout(() => {
      this.isNavigating = false;
    }, 300);
  }

  // Check if a navigation item is active
  isNavigationItemActive(item: NavigationItem, index: number): boolean {
    // Use local active state if set, otherwise fall back to item's active property
    if (this.activeNavigationIndex !== null) {
      return index === this.activeNavigationIndex;
    }
    return item.active === true;
  }

  /**
   * Handle CTA button click: prevents default, stops propagation, resolves the CTA link to a pageType and emits navigationClick, then closes mobile menu.
   * @example
   * nCTAButtonClick(new Event('click'))
   * undefined
   * @param {{Event}} {{event}} - Click event to prevent default and stop propagation.
   * @returns {{void}} Emits navigationClick with resolved pageType and closes mobile menu.
   **/
  onCTAButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.ctaButton?.link) return;

    // Convert link to page type - handle both hash links (#expresswashes) and regular links
    const link = this.ctaButton.link;
    // Remove hash, leading/trailing slashes and normalize
    const normalizedLink = link
      .replace(/^#+|\/+$/g, '')
      .replace(/^\/+/, '')
      .toLowerCase();

    let pageType = 'home';
    if (normalizedLink && normalizedLink !== 'home' && normalizedLink !== '') {
      // Handle hash links like #expresswashes, #expressdetails, #fleets, #coupons
      if (
        normalizedLink === 'expresswashes' ||
        normalizedLink.includes('express-wash') ||
        normalizedLink === 'express-washes'
      ) {
        pageType = 'express-wash-packages';
      } else if (
        normalizedLink === 'expressdetails' ||
        normalizedLink.includes('express-detail') ||
        normalizedLink.includes('express-auto') ||
        normalizedLink === 'express-details'
      ) {
        pageType = 'express-auto-details';
      } else if (normalizedLink === 'fleets' || normalizedLink.includes('fleet')) {
        pageType = 'fleet-management';
      } else if (normalizedLink === 'coupons' || normalizedLink.includes('coupon')) {
        pageType = 'coupons';
      } else {
        // Try to match with navigation items
        const matchingNav = this.navigation.find(nav => {
          const navLink = (nav.link || '')
            .replace(/^#+|\/+$/g, '')
            .replace(/^\/+/, '')
            .toLowerCase();
          return navLink === normalizedLink || navLink.replace(/-/g, '') === normalizedLink;
        });
        if (matchingNav && matchingNav.pageType) {
          pageType = matchingNav.pageType;
        }
      }
    }

    this.navigationClick.emit(pageType);
    // Close mobile menu when CTA button is clicked
    this.isMobileMenuOpen = false;
  }

  toggleMobileMenu(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    // Always allow menu to toggle - it should work in both editor and preview mode
    // The mobile menu overlay and drawer are only shown when clickable is false (preview mode)
    // but the hamburger button should always be functional
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  // Close mobile menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Close menu if clicking outside the menu and hamburger button
    if (
      this.isMobileMenuOpen &&
      !target.closest('.mobile-menu-drawer') &&
      !target.closest('.hamburger-menu')
    ) {
      this.closeMobileMenu();
    }
  }

  // Close mobile menu on escape key
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  /**
   * Update the component's activeNavigationIndex when the navigation input changes.
   * @example
   * gOnChanges({ navigation: { currentValue: [{ active: true }, { active: false }] } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - The changes object provided by Angular containing input changes.
   * @returns {{void}} No return value; updates internal activeNavigationIndex state.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Update active state when navigation input changes
    if (changes['navigation'] && this.navigation && this.navigation.length > 0) {
      // Find the active item from the input
      const activeIndex = this.navigation.findIndex(nav => nav.active === true);
      if (activeIndex !== -1) {
        this.activeNavigationIndex = activeIndex;
      } else if (this.activeNavigationIndex === null) {
        // If no active item is set and we haven't set one locally, default to first item
        this.activeNavigationIndex = 0;
      }
    }
  }

  /**
   * Get heading color from branding or use default (white for dark theme)
   */
  getHeadingColor(): string {
    return this.headingColor || '#FFFFFF';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Inter';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
